import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Megaphone, Check, Clock, X, ChevronLeft, ChevronRight, Minus, ChevronUp, CornerDownRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { priorityStyle } from './priorityStyles'
import { renderAnnouncementMarkdown } from './announcementMarkdown'
import type { Announcement, SnoozeKind } from './types'

const CARD_W = 360

function pick(base: string | null | undefined, i18n: Record<string, string> | undefined, lng: string): string {
  return (i18n && i18n[lng]) || base || ''
}

/** Short relative time for the popup meta row. */
function relTime(iso?: string | null, justNow = 'Just now'): string {
  if (!iso) return ''
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) return ''
  const m = Math.floor((Date.now() - ts) / 60_000)
  if (m < 1) return justNow
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

interface Props {
  ann: Announcement
  /** 0-based index of the current item among `total` pending (for the i/N switcher). */
  index: number
  total: number
  onRead: () => void
  onSnooze: (kind: SnoozeKind) => void
  onDismiss: () => void
  /** Switch to the previous/next pending announcement (carousel, no vertical stacking). */
  onPrev: () => void
  onNext: () => void
  /** Minimize: collapse the toast to a compact corner pill (restore on click). */
  collapsed: boolean
  onToggleCollapse: () => void
  /** Dragged position (controlled by the Host so it survives item switches + persists). */
  pos: { left: number; top: number } | null
  onDragMove: (p: { left: number; top: number }) => void
  onDragEnd: (p: { left: number; top: number }) => void
  /** Reset position back to the default bottom-right corner. */
  onResetPos: () => void
}

/**
 * Single fixed bottom-right announcement toast (Windows-notification style) — not
 * resizable; defaults to the bottom-right corner but the header is a drag handle so
 * the user can reposition it. Multiple pending items are NOT tiled vertically; they
 * share one card and are switched via the i/N pager (‹ 1/N ›). Content is rendered
 * inline (compact markdown, scrollable).
 */
export function StickyAnnouncementPopup({ ann, index, total, onRead, onSnooze, onDismiss, onPrev, onNext, collapsed, onToggleCollapse, pos, onDragMove, onDragEnd, onResetPos }: Props) {
  const { t, i18n } = useTranslation('announcements')
  const lng = i18n.language?.split('-')[0] ?? 'en'
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const c = priorityStyle(ann.priority)
  const contentHtml = renderAnnouncementMarkdown(pick(ann.content, ann.contentI18n, lng))

  // Drag: position is controlled by the Host (`pos`); we just report move/end so it
  // can persist. Clamps to viewport; window-level listeners so the drag keeps tracking
  // even if the pointer leaves the card.
  const dragRef = useRef<{ offX: number; offY: number; w: number; h: number; last: { left: number; top: number } } | null>(null)

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return // header buttons keep their click
    const card = e.currentTarget.parentElement as HTMLElement | null
    if (!card) return
    const r = card.getBoundingClientRect()
    const init = { left: r.left, top: r.top }
    dragRef.current = { offX: e.clientX - r.left, offY: e.clientY - r.top, w: r.width, h: r.height, last: init }
    onDragMove(init)
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      const left = Math.min(Math.max(0, ev.clientX - d.offX), window.innerWidth - d.w)
      const top = Math.min(Math.max(0, ev.clientY - d.offY), window.innerHeight - d.h)
      d.last = { left, top }
      onDragMove(d.last)
    }
    const onUp = () => {
      const d = dragRef.current
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (d) onDragEnd(d.last) // persist final position
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    const onDown = (e: PointerEvent) => {
      const el = menuRef.current
      if (el && !el.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [menuOpen])

  // Minimized: compact bottom-right pill (megaphone + count), click to restore.
  // NOTE: must come AFTER all hooks (Rules of Hooks — no early return before hooks).
  if (collapsed) {
    return (
      <button
        onClick={onToggleCollapse}
        title={t('expand', { defaultValue: 'Expand' })}
        style={{ position: 'fixed', right: 22, bottom: 20, zIndex: 1000 }}
        className="flex items-center gap-2 rounded-full border border-border bg-popover py-1.5 pl-1.5 pr-3 shadow-xl hover:bg-accent"
      >
        <span className={cn('grid h-7 w-7 place-items-center rounded-full', c.header)}>
          <Megaphone size={14} />
        </span>
        <span className="text-[12.5px] font-semibold text-foreground">{t('kicker')}</span>
        {total > 0 ? (
          <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">{total}</span>
        ) : null}
        <ChevronUp size={15} className="text-muted-foreground" />
      </button>
    )
  }

  return (
    // Default anchored bottom-right (Windows-toast); drag the header to reposition.
    <div
      style={
        pos
          ? { position: 'fixed', left: pos.left, top: pos.top, width: CARD_W, zIndex: 1000 }
          : { position: 'fixed', right: 22, bottom: 20, width: CARD_W, zIndex: 1000 }
      }
      className="overflow-hidden rounded-2xl border border-border bg-popover shadow-xl"
    >
      {/* priority-colored header — drag handle */}
      <div
        onPointerDown={startDrag}
        className={cn('flex cursor-move select-none items-center gap-2 px-3.5 py-2.5', c.header)}
      >
        <span className="grid h-[26px] w-[26px] place-items-center rounded-lg bg-white/20">
          <Megaphone size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-extrabold uppercase tracking-wider opacity-95">{t('kicker')}</div>
          {ann.category ? <div className="mt-px text-[11px] opacity-90">{ann.category}</div> : null}
        </div>
        <span className="whitespace-nowrap rounded-full bg-white/20 px-2 py-0.5 text-[10.5px] font-bold">
          {t(`priority.${ann.priority}`)}
        </span>
        {pos ? (
          <button
            onClick={onResetPos}
            title={t('resetPosition', { defaultValue: 'Reset to bottom-right' })}
            aria-label={t('resetPosition', { defaultValue: 'Reset to bottom-right' })}
            className="grid h-6 w-6 place-items-center rounded-md bg-white/15 hover:bg-white/25"
          >
            <CornerDownRight size={14} />
          </button>
        ) : null}
        <button
          onClick={onToggleCollapse}
          title={t('minimize', { defaultValue: 'Minimize' })}
          aria-label={t('minimize', { defaultValue: 'Minimize' })}
          className="grid h-6 w-6 place-items-center rounded-md bg-white/15 hover:bg-white/25"
        >
          <Minus size={15} />
        </button>
        <button
          onClick={onDismiss}
          title={t('dismiss')}
          aria-label={t('dismiss')}
          className="grid h-6 w-6 place-items-center rounded-md bg-white/15 hover:bg-white/25"
        >
          <X size={15} />
        </button>
      </div>

      {/* body — full content inline, compact font */}
      <div className="px-4 pb-1 pt-3">
        <h3 className="text-sm font-bold text-foreground">{pick(ann.title, ann.titleI18n, lng)}</h3>
        {contentHtml ? (
          <div
            className="prose prose-sm mt-1.5 max-h-[160px] max-w-none overflow-auto text-[12.5px] leading-relaxed text-muted-foreground [&_:where(h1,h2,h3,h4)]:my-1 [&_:where(h1,h2,h3,h4)]:text-[13px] [&_:where(h1,h2,h3,h4)]:font-semibold [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_img]:my-1"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        ) : null}
        {ann.publishedAt ? (
          <div className="mt-2 flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
            <Clock size={11} /> {relTime(ann.publishedAt, t('justNow'))}
          </div>
        ) : null}
      </div>

      {/* pager — switch between pending items (no vertical tiling) */}
      {total > 1 ? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-1.5">
          <button
            onClick={onPrev}
            title={t('prev', { defaultValue: 'Previous' })}
            aria-label={t('prev', { defaultValue: 'Previous' })}
            className="grid h-6 w-6 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-[11px] font-semibold text-muted-foreground">{index + 1}/{total}</span>
          <button
            onClick={onNext}
            title={t('next', { defaultValue: 'Next' })}
            aria-label={t('next', { defaultValue: 'Next' })}
            className="grid h-6 w-6 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      ) : null}

      {/* footer actions */}
      <div className="flex items-center gap-2 px-4 pb-3.5 pt-2">
        <Button onClick={onRead} className="flex-1 gap-1.5">
          <Check size={14} /> {t('gotIt')}
        </Button>
        <div className="relative" ref={menuRef}>
          <Button variant="outline" size="icon" onClick={() => setMenuOpen((v) => !v)} title={t('snooze')} aria-label={t('snooze')}>
            <Clock size={15} />
          </Button>
          {menuOpen && (
            <div className="absolute bottom-11 right-0 z-10 w-44 rounded-lg border border-border bg-popover p-1 shadow-lg">
              {([['1h', t('snooze1h')], ['evening', t('snoozeEvening')], ['tomorrow', t('snoozeTomorrow')]] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => { setMenuOpen(false); onSnooze(k as SnoozeKind) }}
                  className="block w-full rounded-md px-2.5 py-2 text-left text-[13px] font-medium text-foreground hover:bg-accent"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
